// Lets the browser paint (e.g. the selected swatch) before heavy work continues.
function yieldToMain() {
  if (window.scheduler && "yield" in scheduler) return scheduler.yield();
  return new Promise((resolve) => setTimeout(resolve, 0));
}

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    this.renderedKey = null;
    this.renderToken = 0;

    this.defaultOptions = {
      loop: true,
    };

    this.defaultThumbsOptions = {
      loop: false,
      spaceBetween: 20,
      slidesPerView: 4,
      watchSlidesProgress: true,
    };

    this.elements = {
      swiper: this.querySelector(".media-gallery"),
      prevButton: this.querySelector(".media-gallery__button--prev"),
      nextButton: this.querySelector(".media-gallery__button--next"),
      thumbs: this.querySelector(".media-gallery__thumbs"),
    };
    this.elements.mainWrapper = this.elements.swiper?.querySelector(
      ":scope > .media-gallery__wrapper",
    );
    this.elements.thumbsWrapper = this.elements.thumbs?.querySelector(
      ":scope > .media-gallery__wrapper",
    );
    this.productContainer = this.closest("[data-product-featured]");
    this.mainSlideTemplates = Array.from(this.elements.mainWrapper?.children ?? []).map((slide) =>
      slide.cloneNode(true),
    );
    this.thumbSlideTemplates = Array.from(this.elements.thumbsWrapper?.children ?? []).map(
      (slide) => slide.cloneNode(true),
    );
    this.events = [
      {
        el: this,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.productContainer,
        type: "change",
        callback: this.changeHandler.bind(this),
      },
      {
        el: document,
        type: "variant:change",
        callback: this.variantChangeHandler.bind(this),
      },
      {
        el: document,
        type: "DOMContentLoaded",
        callback: this.init.bind(this),
      },
    ];

    this.swiperOptions = {
      ...this.defaultOptions,
    };
    if (this.elements.swiper.dataset.galleryOptions) {
      this.swiperOptions = {
        ...this.defaultOptions,
        ...JSON.parse(this.elements.swiper.dataset.galleryOptions),
      };
    }

    if (this.elements.prevButton && this.elements.nextButton) {
      this.swiperOptions.navigation = {
        nextEl: this.elements.nextButton,
        prevEl: this.elements.prevButton,
      };
    }

    if (this.elements.thumbs) {
      this.thumbsSwiperOptions = {
        ...this.defaultThumbsOptions,
      };
      if (this.elements.thumbs.dataset.galleryOptions) {
        this.thumbsSwiperOptions = {
          ...this.defaultThumbsOptions,
          ...JSON.parse(this.elements.thumbs.dataset.galleryOptions),
        };
      }
      this.swiperOptions.thumbs = {
        swiper: this.elements.thumbs,
      };
    }
    this.mediaZoom = this.querySelector("media-zoom");
  }

  connectedCallback() {
    this.handleEventListeners();
    if (document.readyState !== "loading") {
      this.init();
    }
  }

  disconnectedCallback() {
    this.handleEventListeners("remove");
    this.destroySwipers();
  }

  handleEventListeners(type = "add") {
    for (const event of this.events) {
      if (!event.el) continue;
      if (type === "add") {
        event.el.addEventListener(event.type, event.callback);
      } else {
        event.el.removeEventListener(event.type, event.callback);
      }
    }
  }

  clickHandler(e) {
    if (e.target.classList.contains("media-gallery__play")) {
      this.videoSlide = e.target.closest(".media-gallery__slide-video");
      this.videoSlideMedia = this.videoSlide.querySelector("video");
      this.featuredVideo = document.querySelector("featured-video");
      if (this.videoSlideMedia) {
        this.playMedia(e);
      } else if (this.featuredVideo) {
        this.scrollToVideo();
      }
    }
  }

  // On page load, keep the server-rendered slides (including the already-painted
  // main image) and only remove the hidden ones, instead of rebuilding everything.
  init() {
    if (!this.elements.swiper || this.swiper) return;
    this.removeHiddenSlides();
    this.createSwipers();
  }

  removeHiddenSlides() {
    [this.elements.mainWrapper, this.elements.thumbsWrapper].forEach((wrapper) => {
      wrapper?.querySelectorAll(":scope > [hidden]").forEach((slide) => slide.remove());
    });
    this.renderedKey = this.slidesKey(this.filterTemplates(this.mainSlideTemplates));
  }

  createSwipers(preferredMediaId = "") {
    if (!this.elements.mainWrapper?.children.length) return;

    if (this.elements.thumbs && this.elements.thumbsWrapper?.children.length) {
      this.thumbsSwiper = new Swiper(this.elements.thumbs, this.thumbsSwiperOptions);
      this.swiperOptions.thumbs.swiper = this.thumbsSwiper;
    }
    this.swiper = new Swiper(this.elements.swiper, this.swiperOptions);
    this.swiper.on("slideChange", () => this.updateActiveMedia());

    const preferredIndex = Array.from(this.swiper.slides).findIndex(
      (slide) => slide.dataset.mediaId === preferredMediaId,
    );
    if (preferredIndex >= 0) {
      this.swiper.slideToLoop(preferredIndex, 0, false);
      this.thumbsSwiper?.slideTo(preferredIndex, 0, false);
    }
    this.updateActiveMedia();
  }

  destroySwipers() {
    if (this.swiper && !this.swiper.destroyed) {
      this.swiper.destroy(true, true);
    }
    if (this.thumbsSwiper && !this.thumbsSwiper.destroyed) {
      this.thumbsSwiper.destroy(true, true);
    }
    this.swiper = undefined;
    this.thumbsSwiper = undefined;
  }

  // Returns the template slides that should be visible for a variant
  // (or, with no variant, the slides the server rendered as visible).
  filterTemplates(templates, variant) {
    return templates.filter((slide) => {
      const optionPosition = Number(slide.dataset.mediaOptionPosition);
      const optionValue = slide.dataset.mediaOptionValue;

      if (!variant) return !slide.hidden;
      if (!optionPosition || !optionValue) return true;

      const selectedValue = String(variant.options[optionPosition - 1] ?? "").toLowerCase();
      return optionValue === selectedValue;
    });
  }

  cloneVisible(slides) {
    return slides.map((slide) => {
      const visibleSlide = slide.cloneNode(true);
      visibleSlide.hidden = false;
      return visibleSlide;
    });
  }

  slidesKey(slides) {
    return slides.map((slide) => slide.dataset.mediaId).join(",");
  }

  renderSlides(variant) {
    const mainSlides = this.filterTemplates(this.mainSlideTemplates, variant);
    const thumbSlides = this.filterTemplates(this.thumbSlideTemplates, variant);
    this.renderedKey = this.slidesKey(mainSlides);

    this.elements.mainWrapper?.replaceChildren(...this.cloneVisible(mainSlides));
    this.elements.thumbsWrapper?.replaceChildren(...this.cloneVisible(thumbSlides));
  }

  // Parse the variant data once and reuse it.
  getVariants() {
    if (!this.variants) {
      const json = this.productContainer?.querySelector(".pr_variants_json")?.textContent;
      this.variants = json ? JSON.parse(json) : [];
    }
    return this.variants;
  }

  changeHandler(e) {
    if (!e.target.matches('select[name="id"]')) return;
    const variant = this.getVariants().find(({ id }) => String(id) === e.target.value);
    this.updateVariantMedia(variant);
  }

  variantChangeHandler(e) {
    const variant = e.detail?.variant;
    if (e.target.closest?.("product-card")) return;

    const eventProduct = e.target.closest?.("[data-product-featured]");
    if (eventProduct !== this.productContainer) return;

    this.updateVariantMedia(variant);
  }

  // Move to a slide without rebuilding the gallery.
  goToMedia(mediaId) {
    if (!this.swiper || !mediaId) return;
    const slides = Array.from(this.swiper.slides);
    const slide = slides.find((s) => s.dataset.mediaId === mediaId);
    if (!slide) return;
    const index = Number(slide.dataset.swiperSlideIndex ?? slides.indexOf(slide));
    this.swiper.slideToLoop(index, 0, false);
    this.thumbsSwiper?.slideTo(index, 0, false);
  }

  async updateVariantMedia(variant) {
    if (!variant?.options) return;

    this.updateSaleSticker(variant);
    const featuredId = String(variant.featured_media?.id ?? "");
    const nextKey = this.slidesKey(this.filterTemplates(this.mainSlideTemplates, variant));

    // Same images as now (e.g. a size change): just move to the right slide.
    if (nextKey === this.renderedKey) {
      this.goToMedia(featuredId);
      return;
    }

    // Let the browser paint the swatch selection before the heavy work.
    const token = ++this.renderToken;
    await yieldToMain();
    if (token !== this.renderToken) return; // a newer tap has taken over

    this.destroySwipers();
    this.renderSlides(variant);
    this.createSwipers(featuredId);
  }

  updateSaleSticker(variant) {
    const saleSticker = this.querySelector(".main-product__sale-sticker");
    if (!saleSticker) return;

    const price = Number(variant.price);
    const compareAtPrice = Number(variant.compare_at_price);
    saleSticker.hidden =
      !Number.isFinite(price) || !Number.isFinite(compareAtPrice) || compareAtPrice <= price;
  }

  updateActiveMedia() {
    const activeSlide = this.swiper?.slides[this.swiper.activeIndex];
    if (!activeSlide) return;

    if (this.activeVideo) {
      this.activeVideo.pause();
      this.activeVideo.currentTime = 0;
      this.videoSlide.classList.remove("video-playing");
    }
    if (!this.mediaZoom) return;

    this.mediaZoom.classList.toggle(
      "hide-zoom",
      Boolean(activeSlide.querySelector(".media-gallery__slide-video")),
    );
    // Fixed: the new gallery's images sit in .media-gallery__slide-image, not .luc-media.
    const activeImage = activeSlide.querySelector(".media-gallery__slide-image img");
    const zoomImage = this.mediaZoom.querySelector("[data-zoom-image]");
    if (!activeImage || !zoomImage) return;

    const imageSrc = activeImage.currentSrc || activeImage.src;
    if (!imageSrc) return;
    const slideMediaURL = new URL(imageSrc, window.location.href);
    slideMediaURL.searchParams.set("width", 2560);
    zoomImage.dataset.zoomImage = slideMediaURL;
  }

  playMedia() {
    this.videoSlide.classList.add("video-playing");
    this.activeVideo = this.videoSlideMedia;
    this.videoSlideMedia.play();
  }
  scrollToVideo() {
    const topBar = document.querySelector(".announcement-bar");
    const header = document.querySelector(".site-header");
    this.headerHeight = header?.offsetHeight + topBar?.offsetHeight;
    window.scrollTo({
      top: this.featuredVideo.offsetTop - this.headerHeight,
      behavior: "smooth",
    });
  }
}

customElements.define("media-gallery", MediaGallery);

class MediaZoom extends ModalDialog {
  constructor() {
    super();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge = this.querySelector("[data-zoom-image]");

    this.eventCache = [];
    this.previousDiff = -1;
  }

  addEventListeners() {
    super.addEventListeners();
    this.cartridge.addEventListener("wheel", this.coarseHandler.bind(this));
    this.cartridge.addEventListener("mousedown", this.panStartHandler.bind(this));
    this.cartridge.addEventListener("mouseup", this.panEndHandler.bind(this));
    this.cartridge.addEventListener("mouseleave", this.panEndHandler.bind(this));
    this.cartridge.addEventListener("mousemove", this.panHandler.bind(this));

    this.cartridge.addEventListener("pointerdown", this.pointerDownHandler.bind(this));
    this.cartridge.addEventListener("pointermove", this.pointerMoveHandler.bind(this));
    this.cartridge.addEventListener("pointerup", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointercancel", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointerout", this.pointerUpHandler.bind(this));
    this.cartridge.addEventListener("pointerleave", this.pointerUpHandler.bind(this));
  }

  openModal() {
    super.openModal();
    this.cartridgeWidth = this.cartridge.offsetWidth;
    this.cartridgeHeight = this.cartridge.offsetHeight;

    this.loadImage();
  }

  closeModal() {
    super.closeModal();
    this.x = 50;
    this.y = 50;
    this.s = 100;
    this.cartridge.removeAttribute("style");
  }

  clickHandler(e) {
    super.clickHandler(e);
  }

  setZoom(delta) {
    this.s -= delta;
    this.s = Math.min(Math.max(this.s, 100), 200);
    if (this.cartridgeWidth > this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `auto ${this.s}%`;
    } else if (this.cartridgeWidth < this.cartridgeHeight) {
      this.cartridge.style.backgroundSize = `${this.s}% auto`;
    } else {
      this.cartridge.style.backgroundSize = `${this.s}% ${this.s}%`;
    }
  }

  coarseHandler(e) {
    e.preventDefault();
    if (e.type === "wheel") {
      this.setZoom(e.deltaY);
    }
  }

  panStartHandler(e) {
    if (
      typeof this.y == "undefined" ||
      typeof this.x == "undefined" ||
      typeof this.s == "undefined"
    ) {
      this.x = 50;
      this.y = 50;
      this.s = 100;
    }
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.cartridge.classList.add("panning");
    this.mouseDown = true;
  }

  panEndHandler(e) {
    this.cartridge.classList.remove("panning");
    this.x = this.deltaX;
    this.y = this.deltaY;
    this.mouseDown = false;
  }

  panHandler(e) {
    if (!this.mouseDown) return;
    const deltaX = e.x - this.startX;
    const deltaY = e.y - this.startY;
    const deltaXPercentage = (deltaX / this.cartridgeWidth) * 100;
    const deltaYPercentage = (deltaY / this.cartridgeHeight) * 100;
    this.deltaX = Math.min(Math.max(this.x - deltaXPercentage * (this.s / 100), 0), 100);
    this.deltaY = Math.min(Math.max(this.y - deltaYPercentage * (this.s / 100), 0), 100);
    this.cartridge.style.backgroundPosition = `${this.deltaX}% ${this.deltaY}%`;
  }

  pointerDownHandler(e) {
    this.eventCache.push(e);
    if (this.eventCache.length === 1) {
      this.panStartHandler(e);
    } else {
      this.panEndHandler(e);
    }
  }

  pointerMoveHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache[index] = e;

    if (this.eventCache.length === 2) {
      const currentDiff = Math.abs(this.eventCache[0].clientX - this.eventCache[1].clientX);

      if (this.previousDiff > 0) {
        const delta = this.previousDiff - currentDiff;
        this.setZoom(delta);
      }

      this.previousDiff = currentDiff;
    } else {
      this.panHandler(e);
    }
  }

  pointerUpHandler(e) {
    const index = this.eventCache.findIndex((cachedEvent) => cachedEvent.pointerId === e.pointerId);
    this.eventCache.splice(index, 1);
    if (this.eventCache.length < 2) {
      this.previousDiff = -1;
    }
    if (this.eventCache.length <= 0) {
      this.x = this.deltaX;
      this.y = this.deltaY;
      this.mouseDown = false;
    }
  }

  loadImage() {
    this.cartridge.style.backgroundImage = `url(${this.cartridge.dataset.zoomImage})`;
  }
}

customElements.define("media-zoom", MediaZoom);